import { useState, useEffect } from 'react'
import { Bookmark, Trash2, Plus } from 'lucide-react'
import { useBuilder } from '../context/BuilderContext'

const STORAGE_KEY = 'synxreach_saved_blocks'

function getSavedBlocks() {
  try {
    const data = localStorage.getItem(STORAGE_KEY)
    return data ? JSON.parse(data) : []
  } catch {
    return []
  }
}

function saveSavedBlocks(blocks) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(blocks))
}

export function useSaveBlock() {
  const saveBlock = (block, name) => {
    const savedBlocks = getSavedBlocks()
    const savedBlock = {
      id: `saved-${Date.now()}`,
      name: name || `${block.type} block`,
      type: block.type,
      settings: JSON.parse(JSON.stringify(block.settings)),
      savedAt: new Date().toISOString()
    }
    savedBlocks.push(savedBlock)
    saveSavedBlocks(savedBlocks)
    return savedBlock
  }

  return { saveBlock }
}

export default function SavedBlocks() {
  const [savedBlocks, setSavedBlocks] = useState([])
  const { addBlock } = useBuilder()

  useEffect(() => {
    setSavedBlocks(getSavedBlocks())
  }, [])

  const handleUseBlock = (saved) => {
    const newBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: saved.type,
      settings: JSON.parse(JSON.stringify(saved.settings))
    }
    addBlock(newBlock)
  }

  const handleDelete = (savedId) => {
    const updated = savedBlocks.filter(b => b.id !== savedId)
    saveSavedBlocks(updated)
    setSavedBlocks(updated)
  }

  if (savedBlocks.length === 0) {
    return (
      <div style={{
        padding: '1rem',
        textAlign: 'center',
        color: '#94a3b8',
        fontSize: '0.75rem'
      }}>
        <Bookmark size={20} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
        <p style={{ margin: 0 }}>No saved blocks yet</p>
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.7rem' }}>
          Select a block and click "Save Block" to reuse it later
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
      {savedBlocks.map(saved => (
        <div
          key={saved.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 0.6rem',
            background: '#fafbfc',
            border: '1.5px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '0.75rem',
            transition: 'all 0.15s'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = '#c7d2fe'
            e.currentTarget.style.background = '#f1f5f9'
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = '#e5e7eb'
            e.currentTarget.style.background = '#fafbfc'
          }}
        >
          <div style={{
            width: '28px',
            height: '28px',
            borderRadius: '6px',
            background: 'rgba(99,102,241,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Bookmark size={13} style={{ color: 'var(--color-primary)' }} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {saved.name}
            </div>
            <div style={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'capitalize' }}>
              {saved.type}
            </div>
          </div>

          <button
            onClick={() => handleUseBlock(saved)}
            title="Add to canvas"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              border: '1px solid #c7d2fe',
              borderRadius: '5px',
              background: '#fff',
              color: 'var(--color-primary)',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            <Plus size={12} />
          </button>

          <button
            onClick={() => {
              if (confirm('Delete this saved block?')) handleDelete(saved.id)
            }}
            title="Delete"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              border: '1px solid #fecaca',
              borderRadius: '5px',
              background: '#fff',
              color: '#dc2626',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            <Trash2 size={12} />
          </button>
        </div>
      ))}
    </div>
  )
}
